import React, { useState } from 'react';
import { marked } from 'marked';
import { Copy, Code, FileText } from 'lucide-react';
import type { GenericToolResult, ToolResultRendererProps } from '../../../types/toolResults';
import Tooltip from '../../common/Tooltip';

/**
 * Default fallback renderer for unknown/unrecognized tool results.
 *
 * Provides basic text display with:
 * - Markdown parsing
 * - URL link conversion
 * - Raw JSON toggle
 * - Copy to clipboard
 */
const DefaultTextRenderer: React.FC<ToolResultRendererProps<GenericToolResult>> = ({
  result,
  onCopy,
  className = ''
}) => {
  const [showRaw, setShowRaw] = useState(false);

  const handleCopy = (text: string) => {
    if (onCopy) {
      onCopy(text);
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  // Convert URLs to clickable links
  const convertUrlsToLinks = (text: string): string => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-800 underline">${url}</a>`;
    });
  };

  // Process content with markdown and link conversion
  const processContent = (content: string): string => {
    const withLinks = convertUrlsToLinks(content);
    try {
      return marked.parse(withLinks) as string;
    } catch (error) {
      return withLinks;
    }
  };

  // Format data for display
  const getDisplayContent = (): string => {
    if (showRaw) {
      return JSON.stringify(result.data, null, 2);
    }

    // If data is a string, process as markdown
    if (typeof result.data === 'string') {
      return processContent(result.data);
    }

    // If data is an object, pretty-print as JSON
    return JSON.stringify(result.data, null, 2);
  };

  const displayContent = getDisplayContent();
  const rawContent = JSON.stringify(result.data, null, 2);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header Card */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">{result.toolName}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tool result output</p>
            </div>
          </div>

          {/* Toggle View Button */}
          <Tooltip content={showRaw ? 'Show formatted view' : 'Show raw JSON'}>
            <button
              onClick={() => setShowRaw(!showRaw)}
              className="inline-flex items-center space-x-2 px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
            >
              {showRaw ? (
                <>
                  <FileText className="w-3 h-3" />
                  <span>Formatted</span>
                </>
              ) : (
                <>
                  <Code className="w-3 h-3" />
                  <span>Raw JSON</span>
                </>
              )}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Content Display */}
      <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-2 flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
            {showRaw ? 'Raw JSON Output' : 'Formatted Output'}
          </span>
          <Tooltip content="Copy to clipboard">
            <button
              onClick={() => handleCopy(showRaw ? rawContent : (typeof result.data === 'string' ? result.data : rawContent))}
              className="inline-flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-800 transition-colors"
            >
              <Copy className="w-3 h-3" />
              <span>Copy</span>
            </button>
          </Tooltip>
        </div>

        <div className="p-4 max-h-96 overflow-y-auto">
          {showRaw ? (
            <pre className="text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
              {displayContent}
            </pre>
          ) : typeof result.data === 'string' ? (
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: displayContent }}
            />
          ) : (
            <pre className="text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
              {displayContent}
            </pre>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-xs text-yellow-800">
          <strong>Note:</strong> This tool does not have a custom renderer.
          Displaying generic text output. For better visualization, implement a custom renderer for tool type "{result.toolType}".
        </p>
      </div>
    </div>
  );
};

export default DefaultTextRenderer;
